'use client'

import { type ReactNode } from 'react'
import PrimaryButton from '@/components/content/app/PrimaryButton'
import { processAffiliateLink } from '@/lib/formatting'
import { cn } from '@/lib/utils'
import type { getPrimaryButton } from '@/components/content/app/helpers'
import type { App, Deal } from '@/payload-types'

type DealClaimButtonProps = {
  deal: Deal
  app: Pick<App, 'id' | 'slug' | 'name' | 'affiliateLink' | 'partnershipTier'>
  onClick?: () => void
  className?: string
  children: ReactNode
}

// Shared "Claim" CTA for deal rows rendered off the dedicated deal page (Deals tabs on
// app/blog/alternative/stack pages, the /deals directory, the deals cross-sell widget, and
// deal search results). Mirrors the outbound branch of getPrimaryButton
// (src/components/content/app/helpers.tsx) so a specific deal row fires the same tracked
// outbound click + DealTracker badge as the review-sidebar CTA, instead of navigating away.
export default function DealClaimButton({
  deal,
  app,
  onClick,
  className,
  children,
}: DealClaimButtonProps) {
  const affiliate = processAffiliateLink(app.affiliateLink, app.slug, app.name, app.partnershipTier)

  const outboundButton: ReturnType<typeof getPrimaryButton> = {
    destination: affiliate.url,
    label: 'Claim',
    isReadReview: false,
    icon: 'site-large',
    opensInNewTab: true,
    passReferrerUrl: affiliate.passReferrerUrl,
    isDeal: true,
    sponsored: affiliate.isSponsored,
    desktopIsRequired: !!deal.desktopIsRequired,
  }

  if (deal.desktopIsRequired) {
    const fallbackButton: ReturnType<typeof getPrimaryButton> = {
      ...outboundButton,
      destination: `/deals/${app.slug}`,
      opensInNewTab: false,
      passReferrerUrl: undefined,
      isDeal: false,
      sponsored: undefined,
    }

    function handleFallbackClick(): void {
      // Preserve the original one-click mobile/tablet flow: DealContainer consumes
      // these values on the dedicated page and opens the desktop handoff sheet for
      // this exact deal. The shared DealClaimButton extraction dropped these writes,
      // so only users with a stale claim key saw the sheet once.
      localStorage.setItem('efficient:deal_id', deal.id.toString())
      localStorage.setItem('efficient:claim', 'true')
      onClick?.()
    }

    return (
      <>
        <PrimaryButton
          primaryButton={fallbackButton}
          primaryDeal={deal}
          app={app}
          onClick={handleFallbackClick}
          className={cn(className, 'lg:hidden')}
        >
          {children}
        </PrimaryButton>
        <PrimaryButton
          primaryButton={outboundButton}
          primaryDeal={deal}
          app={app}
          onClick={onClick}
          className={cn(className, 'hidden lg:inline-flex')}
        >
          {children}
        </PrimaryButton>
      </>
    )
  }

  return (
    <PrimaryButton
      primaryButton={outboundButton}
      primaryDeal={deal}
      app={app}
      onClick={onClick}
      className={className}
    >
      {children}
    </PrimaryButton>
  )
}
